import { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom'
import { Book, Zap, ShieldCheck, Lock } from 'lucide-react'
import { trackEvent } from './utils/analytics'
import Navigation from './components/Navigation'
import ZombieHandler from './components/ZombieHandler'
import { useToast } from './context/ToastContext'
import InAppBrowserGuard from './components/InAppBrowserGuard'
const FeedbackModal = lazy(() => import('./components/FeedbackModal'));
const TourOverlay = lazy(() => import('./components/TourOverlay'));
import { useApp } from './context/AppContext'

// Lazy-loaded routes and features
const DynamicLanding = lazy(() => import('./pages/DynamicLanding'));
const Home = lazy(() => import('./features/hangout/Home'));
const InviteManager = lazy(() => import('./features/hangout/InviteManager'));
const Join = lazy(() => import('./pages/Join'));
const CreateHangout = lazy(() => import('./features/hangout/CreateHangout'));
const CreatePoll = lazy(() => import('./features/hangout/CreatePoll'));
const PollResponse = lazy(() => import('./features/hangout/PollResponse'));
const Locations = lazy(() => import('./features/hangout/Locations'));
const Squads = lazy(() => import('./features/squads/Squads'));
const SquadClubhouse = lazy(() => import('./features/squads/SquadClubhouse'));
const EditHangout = lazy(() => import('./features/hangout/EditHangout'));
const ViewHangout = lazy(() => import('./features/hangout/ViewHangout'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const Notifications = lazy(() => import('./pages/Notifications'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const TopicsDirectory = lazy(() => import('./pages/TopicsDirectory'));
const PolicyView = lazy(() => import('./pages/PolicyView'));
const WhatsNew = lazy(() => import('./pages/WhatsNew'));
const About = lazy(() => import('./pages/About'));
const DataDeletionRequest = lazy(() => import('./pages/DataDeletionRequest'));
const Labs = lazy(() => import('./pages/Labs'));
const FeatureGalleryDemo = lazy(() => import('./pages/FeatureGalleryDemo'));
const Contact = lazy(() => import('./pages/Contact'));
const ContactVerify = lazy(() => import('./pages/ContactVerify'));
const MagicLinkVerify = lazy(() => import('./pages/MagicLinkVerify'));

const WikiLayout = lazy(() => import('./layouts/WikiLayout'));
const WikiPage = lazy(() => import('./pages/WikiPage'));

const GarageDashboard = lazy(() => import('./features/garage/GarageDashboard'));
const GlobalSearchBar = lazy(() => import('./features/garage/GlobalSearchBar'));
const VehicleDetail = lazy(() => import('./features/garage/VehicleDetail'));
const WorkshopCenter = lazy(() => import('./features/garage/WorkshopCenter'));
const GaragePublicProfile = lazy(() => import('./features/garage/GaragePublicProfile'));
const VehiclePublicProfile = lazy(() => import('./features/garage/VehiclePublicProfile'));
const ReportingDashboard = lazy(() => import('./features/garage/ReportingDashboard'));
const ProfileManager = lazy(() => import('./features/garage/ProfileManager'));
const IncidentWizard = lazy(() => import('./features/garage/IncidentWizard'));
const IncidentVault = lazy(() => import('./features/garage/IncidentVault'));
const IncidentDetail = lazy(() => import('./features/garage/IncidentDetail'));
const UnifiedFeedPage = lazy(() => import('./features/garage/UnifiedFeedPage'));

const BarkDashboard = lazy(() => import('./features/bark/BarkDashboard'));
const BarkFeed = lazy(() => import('./features/bark/BarkFeed'));
const SharedBarkPost = lazy(() => import('./features/bark/SharedBarkPost'));
const PetDetail = lazy(() => import('./features/bark/PetDetail'));
const BarkDiscover = lazy(() => import('./features/bark/BarkDiscover'));
const HouseholdProfile = lazy(() => import('./features/bark/HouseholdProfile'));
const LostFoundBoard = lazy(() => import('./features/bark/LostFoundBoard'));
const LostPetDetail = lazy(() => import('./features/bark/LostPetDetail'));
const BarkReportingDashboard = lazy(() => import('./features/bark/BarkReportingDashboard'));
const PetPublicProfile = lazy(() => import('./features/bark/PetPublicProfile'));

const DeveloperCoreLayout = lazy(() => import('./layouts/DeveloperCoreLayout'));
const DeveloperDashboard = lazy(() => import('./features/developer/DeveloperDashboard'));
const DeveloperDocs = lazy(() => import('./features/developer/DeveloperDocs'));
const DeveloperLanding = lazy(() => import('./features/developer/DeveloperLanding'));

// Helper for VAPID key conversion
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

function App() {
    const toast = useToast()
    const { appContext, setIsAuthenticated } = useApp()
    const [user, setUser] = useState(() => {
        try {
            const cached = localStorage.getItem('hs_user_cached');
            return cached ? JSON.parse(cached) : null;
        } catch (e) {
            return null;
        }
    })
    const [settings, setSettings] = useState({})
    const [loading, setLoading] = useState(true)
    const [pendingPolicies, setPendingPolicies] = useState([])
    const [showTour, setShowTour] = useState(false)
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [isEmulated, setIsEmulated] = useState(false)
    const [preloadedHome, setPreloadedHome] = useState(null)
    const [showGlobalFeedback, setShowGlobalFeedback] = useState(false)
    const [isLooping, setIsLooping] = useState(false)

    const refreshUser = async () => {
        try {
            const host = window.location.hostname || '';
            const clientTenant = host.includes('wrenchspot') ? 'WRENCH' : (host.includes('barkspot') ? 'BARK' : 'HANGOUT');
            const res = await fetch(`/api/auth/bootstrap?tenant=${clientTenant}`, {
                credentials: 'include',
                headers: { 
                    'x-session-id': sessionStorage.getItem('hs_session_id'),
                    'x-client-tenant': clientTenant
                }
            });
            if (res.ok) {
                const data = await res.json();
                setUser(data.user);
                if (data.user) {
                    setIsAuthenticated(true);
                    try {
                        localStorage.setItem('hs_user_cached', JSON.stringify({
                            id: data.user.id,
                            name: data.user.name,
                            nickname: data.user.nickname,
                            avatar: data.user.avatar
                        }));
                    } catch (e) {}
                } else {
                    setIsAuthenticated(false);
                    try { localStorage.removeItem('hs_user_cached'); } catch (e) {}
                }
            }
        } catch (e) {
            console.error('[App] Failed to refresh user profile:', e);
        }
    };

    useEffect(() => {
        // --- LOOP PROTECTOR (Anti-Thrash for Automated Infinite Reload Loops) ---
        const now = Date.now();
        const lastReload = parseInt(sessionStorage.getItem('last_reload_ts') || '0');
        const count = parseInt(sessionStorage.getItem('reload_count') || '0');

        // Only flag rapid automated reloads (< 1.2s between page loads)
        if (now - lastReload < 1200) {
            const newCount = count + 1;
            sessionStorage.setItem('reload_count', newCount.toString());
            if (newCount > 5) {
                console.error('🛑 REFRESH LOOP DETECTED. Emergency Stop engaged.');
                setIsLooping(true);
            }
        } else {
            sessionStorage.setItem('reload_count', '0');
        }
        sessionStorage.setItem('last_reload_ts', now.toString());
    }, []);

    useEffect(() => {
        // --- SESSION TRACKING (Initialized in main.jsx) ---
        const sessionId = sessionStorage.getItem('hs_session_id');

        // --- BLACK BOX RECORDING (Async Errors) ---
        const handleRejection = (event) => {
            // Filter out non-critical or known noise if needed
            fetch('/api/logs/error', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: `Unhandled Promise: ${event.reason}`,
                    stack: null,
                    component: 'GlobalPromiseHandler',
                    url: window.location.href,
                    version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown'
                })
            }).catch(() => { }); // Prevent loops
        };

        window.addEventListener('unhandledrejection', handleRejection);

        // Global Error Handling from URL (e.g. Redirects from Auth)

        const params = new URLSearchParams(window.location.search);
        const error = params.get('error');

        if (error) {
            // Map codes to friendly messages
            const messages = {
                'enrollment_closed': 'Enrollment is currently closed. Check back later!',
                'invite_invalid': 'This invite link is invalid or expired.',
                'server_error': 'Something went wrong. Please try again.'
            };
            toast(messages[error] || 'An unknown error occurred.', 'error');
            // Strip error from URL
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
        }

        // UTM / Attribution Tracking
        const searchParams = new URLSearchParams(window.location.search);
        const attribution = {};
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref', 'via'].forEach(key => {
            const val = searchParams.get(key);
            if (val) attribution[key] = val;
        });

        // Capture Entry Path if not set (for Landing Page A/B testing)
        if (!sessionStorage.getItem('entry_path')) {
            sessionStorage.setItem('entry_path', window.location.pathname);
            attribution['entry_path'] = window.location.pathname;
        }

        if (Object.keys(attribution).length > 0) {
            console.log('📊 Attribution Captured:', attribution);
            sessionStorage.setItem('attribution_data', JSON.stringify(attribution));
            // Set Cookie for Backend Access (Auth Redirects) - 24h
            document.cookie = `attribution_data=${JSON.stringify(attribution)}; path=/; max-age=86400`;

            // Track the visit immediately
            trackEvent('SESSION_START', attribution);
        }

        // Fetch Bootstrap Data (Consolidated User, Settings, Policies)
        // Check if pre-fetch happened in index.html
        const initBootstrap = async () => {
            let data = null;
            if (window.__BOOTSTRAP_PROMISE__) {
                try {
                    data = await window.__BOOTSTRAP_PROMISE__;
                    if (data) {
                        console.log('[Bootstrap] Successfully retrieved pre-fetched bootstrap data.');
                    }
                } catch (e) {
                    console.warn('[Bootstrap] Pre-fetched bootstrap promise failed, falling back to direct fetch.', e);
                }
            }

            if (!data) {
                console.log('[Bootstrap] No pre-fetched bootstrap data found (or it was null/failed). Performing direct fetch...');
                try {
                    const host = window.location.hostname || '';
                    const clientTenant = host.includes('wrenchspot') ? 'WRENCH' : (host.includes('barkspot') ? 'BARK' : 'HANGOUT');
                    const res = await fetch(`/api/auth/bootstrap?tenant=${clientTenant}`, {
                        credentials: 'include',
                        headers: { 
                            'x-session-id': sessionStorage.getItem('hs_session_id'),
                            'x-client-tenant': clientTenant
                        }
                    });
                    if (res.ok) {
                        data = await res.json();
                    } else {
                        console.error('[Bootstrap] Direct bootstrap fetch returned status:', res.status);
                    }
                } catch (err) {
                    console.error('[Bootstrap] Direct bootstrap fetch failed:', err);
                }
            }

            if (!data) {
                console.error('[Bootstrap] Critical: Failed to load bootstrap data from both pre-fetch and direct fallback.');
                setLoading(false);
                return;
            }

            const { user: userData, settings: settingsData, pendingPolicies: policyData } = data;

            setUser(userData);
            if (userData) {
                setIsAuthenticated(true);
                try {
                    localStorage.setItem('hs_user_cached', JSON.stringify({
                        id: userData.id,
                        name: userData.name,
                        nickname: userData.nickname,
                        avatar: userData.avatar
                    }));
                } catch (e) {}
            } else {
                setIsAuthenticated(false);
                try { localStorage.removeItem('hs_user_cached'); } catch (e) {}
            }
            setSettings(settingsData || {});

            if (userData && Array.isArray(policyData)) {
                setPendingPolicies(policyData);
            }

            // Dynamic Branding
            const host = window.location.hostname;
            const isWrench = host.includes('wrenchspot');
            const suffix = isWrench ? '| Wrench Spot' : (settingsData['SEO_TITLE_SUFFIX'] || '| The Hangout Spot');
            const publicUrl = window.location.origin;

            // 1. Title
            if (!document.title.includes(suffix)) {
                document.title = (document.title.split('|')[0].trim() || 'Home') + ' ' + suffix;
            }

            // 2. Meta Tags (OG / Twitter)
            updateMeta(settingsData, publicUrl, isWrench);

            // 3. SEO Injections (Header/Footer)
            if (settingsData['HEAD_INJECTION']) {
                injectHTML(settingsData['HEAD_INJECTION'], document.head);
            }
            if (settingsData['FOOTER_INJECTION']) {
                injectHTML(settingsData['FOOTER_INJECTION'], document.body);
            }

            if (data.homePage) {
                setPreloadedHome(data.homePage);
            }

            setLoading(false);
        };

        const injectHTML = (html, target) => {
            try {
                // Check if already injected to prevent dupes on re-renders?
                // Since this runs once on mount, it's fairly safe.
                // But strict mode might trigger twice.
                if (target.getAttribute('data-seo-injected')) return;

                const range = document.createRange();
                range.selectNode(target);
                const fragment = range.createContextualFragment(html);
                target.appendChild(fragment);
                target.setAttribute('data-seo-injected', 'true');
            } catch (e) {
                console.error('SEO Injection Failed', e);
            }
        };

        initBootstrap();

        // Check for Emulation Mode
        if (document.cookie.split('; ').find(row => row.startsWith('is_emulated='))) {
            setIsEmulated(true);
        }

        // PWA Install Logic
        const handleInstallPrompt = (e) => {
            console.log('✅ PWA Install Prompt Captured');
            e.preventDefault();
            window.deferredPrompt = e;
        };
        window.addEventListener('beforeinstallprompt', handleInstallPrompt);

        // Global PWA Installation Tracking
        const handleAppInstalled = () => {
            console.log('✅ PWA Installed Event Captured');
            try {
                localStorage.setItem('pwa_installed', 'true');
                localStorage.setItem(`pwa_installed_${window.location.origin}`, 'true');
            } catch (e) {}
            trackEvent('PWA_INSTALLED');
        };
        window.addEventListener('appinstalled', handleAppInstalled);

        // DEBUG: Listen for SW messages
        const handleSWMessage = (event) => {
            if (event.data && event.data.type === 'PUSH_DEBUG') {
                console.log('🔔 [PAGE] Push Signal Received via SW:', event.data.payload);
                // Force a visible toast in the app
                import('./context/ToastContext').then(() => {
                    // Since we can't easily hook into the context from outside without hacking, 
                    // we'll just alert purely for debugging if needed, or stick to console.
                    // actually, let's just log loudly.
                    console.log('%c PUSH RECEIVED ', 'background: #222; color: #bada55; font-size: 20px');
                });
            }
        };

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', handleSWMessage);
        }

        return () => {
            window.removeEventListener('unhandledrejection', handleRejection);
            window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.removeEventListener('message', handleSWMessage);
            }
        };
    }, []);

    // Re-check pending policies when active workspace context changes
    useEffect(() => {
        if (!user) return;
        const checkPendingPolicies = async () => {
            try {
                const res = await fetch(`/api/auth/policies/pending?tenant=${appContext}`, {
                    headers: { 'x-client-tenant': appContext }
                });
                if (res.ok) {
                    const policies = await res.json();
                    if (Array.isArray(policies)) {
                        setPendingPolicies(policies);
                    }
                }
            } catch (e) {
                console.error('[App] Failed to check pending policies:', e);
            }
        };
        checkPendingPolicies();
    }, [appContext, user?.id]);

    // Decoupled Plausible Analytics: Only track unauthenticated guest/landing page traffic.
    useEffect(() => {
        console.log('[Analytics] Plausible useEffect triggered. Loading:', loading, 'User:', user ? 'Authenticated' : 'Guest');
        if (loading) return;

        // If the user is logged in/inside the app dashboard, do not run Plausible.
        if (user) {
            const existingScript = document.getElementById('plausible-script');
            if (existingScript) {
                existingScript.remove();
                console.log('[Analytics] Decoupled Plausible script removed for authenticated session.');
            }
            return;
        }

        const host = window.location.hostname;
        const isWrench = host.includes('wrenchspot');
        const isBark = host.includes('barkspot');
        const isBracket = host.includes('bracketspot');
        const domain = isWrench ? 'wrenchspot.app' : (isBark ? 'barkspot.app' : (isBracket ? 'bracketspot.app' : 'thehangoutspot.com'));

        console.log(`[Analytics] Preparing Plausible script for host: ${host}, detected domain: ${domain}`);

        // Pre-initialize the plausible function queue for custom event/goal tracking (e.g. CTA Clicks)
        window.plausible = window.plausible || function() {
            (window.plausible.q = window.plausible.q || []).push(arguments);
        };

        let el = document.getElementById('plausible-script');
        if (!el) {
            el = document.createElement('script');
            el.id = 'plausible-script';
            el.async = true; // Use async instead of defer for dynamic script injection
            el.setAttribute('data-domain', domain);
            el.setAttribute('data-api', '/api/event');
            el.src = '/js/script.js?v=2';
            
            el.onload = () => {
                console.log(`[Analytics] Plausible script loaded successfully for domain: ${domain}`);
            };
            el.onerror = (err) => {
                console.error(`[Analytics] Plausible script failed to load for domain: ${domain}`, err);
            };

            document.head.appendChild(el);
            console.log(`[Analytics] Dynamic Plausible tracker script appended to document.head for domain: ${domain}`);
        } else {
            console.log('[Analytics] Plausible script already exists in DOM.');
        }
    }, [loading, user]);

    // PWA Self-Healing: Auto-Resubscribe if permission granted but subscription missing or invalid/expired on backend
    useEffect(() => {
        // Guard: Only run if user is logged in
        if (!user) return;

        const healPushSubscription = async () => {
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

            // Only heal if user explicitly granted permission previously
            if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

            const createFreshSubscription = async (reg, publicKey) => {
                try {
                    let activeKey = publicKey;
                    if (!activeKey) {
                        const vapidRes = await fetch('/api/notifications/vapid-key');
                        if (!vapidRes.ok) return;
                        const data = await vapidRes.json();
                        activeKey = data.publicKey;
                    }

                    // Subscribe
                    const newSub = await reg.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: urlBase64ToUint8Array(activeKey)
                    });

                    // Send to backend
                    const subRes = await fetch('/api/notifications/subscribe', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(newSub.toJSON ? newSub.toJSON() : newSub)
                    });
                    if (!subRes.ok) throw new Error('Server rejected subscription');
                    console.log('[PWA] ✅ Successfully created fresh push subscription.');
                } catch (subErr) {
                    console.error('[PWA] Failed to create fresh subscription:', subErr);
                }
            };

            try {
                const reg = await navigator.serviceWorker.ready;
                const sub = await reg.pushManager.getSubscription();

                // Fetch VAPID key to verify if it has been rotated
                const vapidRes = await fetch('/api/notifications/vapid-key');
                let serverPublicKey = null;
                if (vapidRes.ok) {
                    const data = await vapidRes.json();
                    serverPublicKey = data.publicKey;
                }

                let keysMatch = true;
                if (sub && serverPublicKey) {
                    try {
                        const serverKeyArray = urlBase64ToUint8Array(serverPublicKey);
                        const subKeyArray = new Uint8Array(sub.options.applicationServerKey);
                        keysMatch = (serverKeyArray.length === subKeyArray.length) &&
                                    serverKeyArray.every((val, i) => val === subKeyArray[i]);
                    } catch (keyErr) {
                        console.error('[PWA] Error checking VAPID key mismatch:', keyErr);
                    }
                }

                if (!sub) {
                    console.log('[PWA] 🩹 Permission granted but no subscription found. Attempting to heal...');
                    await createFreshSubscription(reg, serverPublicKey);
                } else if (!keysMatch) {
                    console.log('[PWA] ⚠️ VAPID keys rotated or mismatch detected. Re-subscribing with fresh key...');
                    try {
                        await sub.unsubscribe();
                    } catch (unsubErr) {
                        console.warn('[PWA] Unsubscribe failed (ignoring):', unsubErr);
                    }
                    await createFreshSubscription(reg, serverPublicKey);
                } else {
                    // Verification phase: Check if backend knows about this endpoint
                    try {
                        const verifyRes = await fetch('/api/notifications/verify-subscription', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ endpoint: sub.endpoint })
                        });
                        
                        if (verifyRes.ok) {
                            const verifyData = await verifyRes.json();
                            if (!verifyData.active) {
                                console.log('[PWA] ⚠️ Subscription is invalid/missing on server (possibly 410ed/wiped). Re-subscribing...');
                                // Unsubscribe old one
                                try {
                                    await sub.unsubscribe();
                                } catch (unsubErr) {
                                    console.warn('[PWA] Unsubscribe failed (ignoring):', unsubErr);
                                }
                                // Create fresh one!
                                await createFreshSubscription(reg, serverPublicKey);
                            } else {
                                console.log('[PWA] ✅ Push subscription verified active on server.');
                            }
                        } else {
                            // Fallback silent heal in case verify endpoint itself is failing
                            await fetch('/api/notifications/subscribe', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(sub.toJSON ? sub.toJSON() : sub)
                            });
                        }
                    } catch (verifyErr) {
                        console.warn('[PWA] Verification failed, falling back to silent heal:', verifyErr);
                        await fetch('/api/notifications/subscribe', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(sub.toJSON ? sub.toJSON() : sub)
                        });
                    }
                }
            } catch (err) {
                console.warn('[PWA] Failed to heal subscription:', err);
            }
        };

        // Delay 3 seconds to ensure SW is fully active and out of the critical rendering path
        const timer = setTimeout(healPushSubscription, 3000);
        return () => clearTimeout(timer);
    }, [user]);

    // Location Tracking (IP Fallback - No Permissions Needed)
    useEffect(() => {
        if (user) {
            const syncLocation = async () => {
                // Rate Limit Check (1 hour cache)
                const lastSync = localStorage.getItem('loc_sync_ts');
                if (lastSync && (Date.now() - parseInt(lastSync) < 3600000)) return;

                try {
                    // Server-Side Detection
                    const res = await fetch('/api/auth/detect-location', { 
                        method: 'POST',
                        headers: { 'x-session-id': sessionStorage.getItem('hs_session_id') }
                    });
                    if (!res.ok) return;

                    const data = await res.json();

                    if (data.lat && data.lng) {
                        // Cache Success
                        localStorage.setItem('loc_sync_ts', Date.now().toString());
                        // Store detailed info for Analytics
                        localStorage.setItem('user_location', JSON.stringify({
                            city: data.city,
                            region: data.region,
                            country: data.country
                        }));
                    }
                } catch (e) {
                    console.warn('Location sync skipped:', e);
                }
            };

            const syncDeviceStatus = async () => {
                // Check PWA & Push Status
                const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
                const pushPermission = 'Notification' in window ? Notification.permission : 'unsupported';

                // Debounce/Cache: Only send if changed, OR if 24 hours have passed since last sync
                const cacheKey = `device_status_${isStandalone}_${pushPermission}`;
                const hasCachedStatus = localStorage.getItem('device_status_cache') === cacheKey;
                const lastSync = localStorage.getItem('device_status_sync_ts');
                const timeDiff = lastSync ? Date.now() - parseInt(lastSync, 10) : Infinity;

                if (hasCachedStatus && timeDiff < 24 * 60 * 60 * 1000) {
                    return;
                }

                try {
                    await fetch('/api/users/device-status', {
                        method: 'POST',
                        headers: { 
                            'Content-Type': 'application/json',
                            'x-session-id': sessionStorage.getItem('hs_session_id')
                        },
                        body: JSON.stringify({
                            hasPwaInstalled: isStandalone,
                            config: {
                                pushPermission,
                                userAgent: navigator.userAgent,
                                screen: { width: window.screen.width, height: window.screen.height }
                            }
                        })
                    });
                    localStorage.setItem('device_status_cache', cacheKey);
                    localStorage.setItem('device_status_sync_ts', Date.now().toString());
                } catch (e) {
                    console.warn('Device Sync Failed', e);
                }
            };

            syncLocation();
            syncDeviceStatus();
        }
    }, [user]);

    const updateMeta = (settings, url, isWrench) => {
        const origin = url || window.location.origin;
        const pageUrl = `${origin}${window.location.pathname}`;
        const title = isWrench ? `Wrench Spot` : (appContext === 'BARK' ? `Bark Spot` : `The Hangout Spot ${settings['SEO_TITLE_SUFFIX'] || ''}`);
        const desc = isWrench
            ? "The ultimate companion app for auto enthusiasts. Track modifications, service logs, maintenance reminders, and share your public garage showcase."
            : (appContext === 'BARK'
                ? "The ultimate social network and wellness logger for your pets. Connect with other pets, share updates, and track meals, medicine, or health incidents."
                : "The ultimate private social hub for your squad.");
        const image = isWrench ? `${origin}/wrench-og-image.png` : (appContext === 'BARK' ? `${origin}/barkspot-og-image.png` : `${origin}/og-image.png`);

        const tags = {
            'description': desc,
            'og:title': title,
            'og:description': desc,
            'og:url': pageUrl,
            'og:image': image,
            'twitter:card': 'summary_large_image',
            'twitter:title': title,
            'twitter:description': desc,
            'twitter:image': image
        };

        Object.entries(tags).forEach(([name, content]) => {
            let el = document.querySelector(`meta[property="${name}"]`) || document.querySelector(`meta[name="${name}"]`);
            if (!el) {
                el = document.createElement('meta');
                if (name.startsWith('og:')) el.setAttribute('property', name);
                else el.setAttribute('name', name);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        });

        let canonicalEl = document.querySelector('link[rel="canonical"]');
        if (!canonicalEl) {
            canonicalEl = document.createElement('link');
            canonicalEl.setAttribute('rel', 'canonical');
            document.head.appendChild(canonicalEl);
        }
        canonicalEl.setAttribute('href', pageUrl);
    };

    const handleAccept = async () => {
        // 1. If name is missing, we must save it first (or concurrently)
        if (!user.name && (firstName?.trim() || lastName?.trim())) {
            try {
                const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
                const res = await fetch(`/api/users/${user.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: fullName })
                });
                if (!res.ok) throw new Error("Failed to update name");
                setUser(prev => ({ ...prev, name: fullName }));
            } catch (err) {
                console.error("Failed to save name:", err);
                toast("Failed to save name. Please try again.", "error");
                return;
            }
        }

        if (pendingPolicies.length === 0) return;
        const policy = pendingPolicies[0];
        try {
            await fetch('/api/auth/policies/accept', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ policyId: policy.id })
            });
            setPendingPolicies(prev => {
                const updated = prev.slice(1);
                // TOUR TRIGGER REMOVED FROM HERE (Delayed until Home page)
                return updated;
            });
            toast('Policy accepted!', 'success');
        } catch (err) {
            toast('Failed to accept policy. Please try again.', 'error');
        }
    };

    if (isLooping) {
        return (
            <div className="min-h-screen bg-neutral-950 text-white flex flex-col items-center justify-center p-6 text-center space-y-6 font-mono">
                <div className="w-20 h-20 rounded-3xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500">
                    <ShieldCheck size={40} />
                </div>
                <h1 className="text-2xl font-black tracking-tighter uppercase text-red-500">Emergency Stop</h1>
                <p className="text-neutral-400 max-w-sm text-sm">
                    We detected a refresh loop. This is usually caused by a hardware/browser conflict. 
                    Your session has been paused to prevent excessive server requests.
                </p>
                <div className="flex flex-col gap-3">
                    <button 
                        onClick={() => {
                            sessionStorage.removeItem('reload_count');
                            sessionStorage.removeItem('last_reload_ts');
                            window.location.href = '/';
                        }}
                        className="bg-white text-black px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-xs"
                    >
                        Try Manual Reset
                    </button>
                    <a href="/api/auth/logout" className="text-neutral-600 text-[10px] uppercase font-bold hover:text-white transition">Logout & Clear Session</a>
                </div>
            </div>
        )
    }

    if (loading) {
        return <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center font-mono">Initializing System...</div>
    }

    // Maintenance Mode (Block all non-admins)
    const isMaintenance = settings['MAINTENANCE_MODE'] === 'true';
    const isAdmin = user && (user.role === 'ADMIN' || user.role === 'SUPERADMIN');

    if (isMaintenance && !isAdmin) {
        return (
            <div className="min-h-screen bg-neutral-950 text-white flex flex-col items-center justify-center p-6 text-center space-y-6">
                <div className="w-20 h-20 rounded-3xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500 animate-pulse">
                    <Zap size={40} />
                </div>
                <h1 className="text-4xl font-black tracking-tighter">We'll be back</h1>
                <p className="text-neutral-400 max-w-sm">
                    The Hangout Spot is currently undergoing planned maintenance. Keep hangin', we'll be back shortly!
                </p>
                <div className="pt-4">
                    <a 
                        href={`/api/auth/logout?source=${appContext === 'WRENCH' ? 'wrenchspot' : 'hangoutspot'}`}
                        onClick={() => trackEvent('LOGOUT', { reason: 'maintenance_exit' })}
                        className="text-xs text-neutral-600 uppercase font-black hover:text-white transition"
                    >Admin Login</a>
                </div>
            </div>
        )
    }

    const showLanding = settings['LANDING_PAGE'] === 'true' && !user;

    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <RedirectHandler />
            <ZombieHandler user={user} />
            <InAppBrowserGuard />
            <TourTrigger user={user} pendingPolicies={pendingPolicies} showTour={showTour} setShowTour={setShowTour} />
            {/* Emulation Banner */}
            {isEmulated && (
                <div className="bg-red-600 text-white text-xs font-bold p-2 text-center sticky top-0 z-[100] flex justify-between items-center px-4 shadow-xl">
                    <span className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                        EMULATING MODE: {user?.name}
                    </span>
                    <a 
                        href={`/api/auth/logout?source=${appContext === 'WRENCH' ? 'wrenchspot' : 'hangoutspot'}`}
                        onClick={() => trackEvent('LOGOUT', { reason: 'emulation_exit' })}
                        className="bg-white text-red-600 px-3 py-1 rounded uppercase tracking-wider hover:bg-red-50 transition"
                    >
                        Logout / Return
                    </a>
                </div>
            )}
            {/* Guided Tour */}
            {user && showTour && !pendingPolicies.length && (
                <TourOverlay onComplete={async () => {
                    // Optimistic Update: Prevent "Double Tour" race condition
                    // We must update the user state BEFORE (or with) hiding the tour so TourTrigger doesn't fire again
                    setUser(prev => ({ ...prev, hasSeenTour: true }));
                    setShowTour(false);

                    try {
                        await fetch('/api/users/tour-complete', { method: 'POST' });
                    } catch (err) {
                        console.error('Failed to save tour progress');
                    }
                }} />
            )}

            {/* Policy Modal (Force Acceptance) */}
            {user && (pendingPolicies.length > 0 || !user.name) && (
                <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4">
                    <div className="bg-neutral-900 border border-neutral-700 rounded-3xl max-w-2xl w-full flex flex-col max-h-[85vh] shadow-2xl animate-fade-in ring-1 ring-white/10">
                        <div className="p-8 border-b border-neutral-800 bg-neutral-900 rounded-t-3xl">
                            <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 mb-4">
                                <ShieldCheck size={24} />
                            </div>
                            <h2 className="text-3xl font-black text-white tracking-tight leading-none mb-2">
                                {!user.name ? "Welcome to the Spot" : "Legal Update Required"}
                            </h2>
                            <p className="text-neutral-400 text-sm">
                                {!user.name
                                    ? "Before you jump in, what should we call you? We also need you to accept our latest terms."
                                    : `To continue using the platform, please review and accept the latest ${pendingPolicies[0]?.type === 'BARK_TERMS' ? 'Bark Spot Terms of Service' : (pendingPolicies[0]?.type === 'TERMS' ? 'Terms of Service' : (pendingPolicies[0]?.type === 'PRIVACY' ? 'Privacy Policy' : (pendingPolicies[0]?.type || 'policy')))}.`}
                            </p>
                        </div>

                        <div className="flex-grow overflow-y-auto p-8 space-y-6">
                            {!user.name && (
                                <div className="space-y-6">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-[10px] font-black text-purple-400 border border-purple-500/20">1</div>
                                        <h3 className="text-sm font-black uppercase tracking-widest text-white">About You</h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-neutral-500">First Name</label>
                                            <input
                                                type="text"
                                                placeholder="First Name"
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                                className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl p-4 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition font-bold"
                                                autoFocus
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-neutral-500">Last Name</label>
                                            <input
                                                type="text"
                                                placeholder="Last Name"
                                                value={lastName}
                                                onChange={(e) => setLastName(e.target.value)}
                                                className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl p-4 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition font-bold"
                                            />
                                        </div>
                                    </div>

                                    <div className="h-px bg-neutral-800 w-full my-8" />
                                </div>
                            )}

                            {pendingPolicies.length > 0 && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-[10px] font-black text-purple-400 border border-purple-500/20">2</div>
                                        <h3 className="text-sm font-black uppercase tracking-widest text-white">
                                            {pendingPolicies[0]?.type === 'BARK_TERMS' ? 'Bark Spot Terms of Service' : (pendingPolicies[0]?.type === 'PRIVACY' ? 'Privacy Policy' : 'Legal Agreement')}
                                        </h3>
                                    </div>
                                    <div className="bg-neutral-950 border border-neutral-800 rounded-2xl p-6 font-mono text-xs text-neutral-400 leading-relaxed max-h-[200px] overflow-y-auto custom-scrollbar whitespace-pre-wrap">
                                        <h3 className="text-white font-bold mb-2 uppercase tracking-widest">
                                            {pendingPolicies[0].name || (pendingPolicies[0].type === 'BARK_TERMS' ? 'Bark Spot Terms of Service' : (pendingPolicies[0].type === 'PRIVACY' ? 'Privacy Policy' : 'Terms of Service'))}
                                        </h3>
                                        {pendingPolicies[0].content}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-8 border-t border-neutral-800 bg-neutral-900 rounded-b-3xl flex items-center justify-center gap-4">
                            <button
                                onClick={() => {
                                    trackEvent('LOGOUT', { reason: 'policy_decline' });
                                    window.location.href = `/api/auth/logout?source=${appContext === 'WRENCH' ? 'wrenchspot' : (appContext === 'BARK' ? 'barkspot' : (appContext === 'DEVELOPER' ? 'developer' : 'hangoutspot'))}`;
                                }}
                                className="text-neutral-500 hover:text-red-400 font-bold transition text-xs tracking-widest uppercase"
                            >
                                Decline & Logout
                            </button>
                            <button
                                onClick={handleAccept}
                                disabled={(!user.name && (!firstName.trim() || !lastName.trim()))}
                                className="bg-white text-black hover:bg-neutral-200 px-8 py-3 rounded-2xl font-black shadow-xl transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:scale-100"
                            >
                                {!user.name ? "Save & Continue" : "I Accept"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Global Feedback Modal (Deep Link) */}
            {showGlobalFeedback && (
                <Suspense fallback={null}>
                    <FeedbackModal onClose={() => setShowGlobalFeedback(false)} defaultTab="HISTORY" />
                </Suspense>
            )}

            <div className={`min-h-screen bg-neutral-950 text-white font-sans ${!showLanding ? 'pb-24' : ''}`}>
                <RedirectHandler />
                <WorkspaceContextWatcher user={user} settings={settings} />
                <main>
                    <Suspense fallback={<div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center font-mono animate-pulse">Loading...</div>}>
                        <Routes>
                            {/* Public Routes */}
                            <Route path="/wiki" element={<WikiLayout />}>
                                <Route index element={
                                    <div className="flex flex-col items-center justify-center min-h-[50vh] text-neutral-500 text-center p-6 space-y-4">
                                        <div className="w-16 h-16 bg-purple-500/10 rounded-2xl flex items-center justify-center text-purple-400">
                                            <Book size={32} />
                                        </div>
                                        <div>
                                            <h3 className="text-white font-bold text-lg">Knowledge Base</h3>
                                            <p className="text-sm max-w-xs mx-auto mt-1">Select a topic from the sidebar to view documents, guides, and roadmaps.</p>
                                        </div>
                                    </div>
                                } />
                                <Route path="*" element={<WikiPage />} />
                            </Route>
                            <Route path="/landing" element={<DynamicLanding slug="home" settings={settings} preloadedPage={preloadedHome} />} />
                            <Route path="/policies/:type" element={<PolicyView />} />
                            <Route path="/privacy/data-deletion" element={user ? <DataDeletionRequest user={user} /> : <Navigate to="/" />} />
                            <Route path="/contact" element={<Contact user={user} />} />
                            <Route path="/contact/verify" element={<ContactVerify />} />
                            <Route path="/verify" element={<MagicLinkVerify />} />
                            <Route path="/public/garage/:userId" element={<GaragePublicProfile />} />
                            <Route path="/public/vehicle/:vehicleId" element={<VehiclePublicProfile />} />

                            {/* Main Logic - Priority to pending post-login deep links */}
                            <Route path="/" element={
                                (() => {
                                    const searchParams = new URLSearchParams(window.location.search);
                                    const rawPostRedirect = searchParams.get('redirect') ||
                                        sessionStorage.getItem('post_login_redirect') || 
                                        localStorage.getItem('post_login_redirect') || 
                                        (document.cookie.match(/(?:^|;\s*)post_login_redirect=([^;]*)/)?.[1]);
                                    
                                    const decodedPostRedirect = rawPostRedirect ? decodeURIComponent(rawPostRedirect) : null;

                                    if (decodedPostRedirect && decodedPostRedirect.startsWith('/bark/post/')) {
                                        sessionStorage.removeItem('post_login_redirect');
                                        localStorage.removeItem('post_login_redirect');
                                        document.cookie = 'post_login_redirect=; path=/; max-age=0';
                                        return <Navigate to={decodedPostRedirect} replace />;
                                    }

                                    if (!user || showLanding) {
                                        return <DynamicLanding slug="home" settings={settings} preloadedPage={preloadedHome} />;
                                    }

                                    const invite = searchParams.get('invite');
                                    if (invite) {
                                        return <Navigate to={`/join/${invite}`} replace />;
                                    }

                                    const context = localStorage.getItem('hs_app_context') || appContext;
                                    if (context === 'WRENCH') return <Navigate to="/garage" replace />;
                                    if (context === 'BARK') return <Navigate to="/bark" replace />;
                                    return <Home user={user} settings={settings} />;
                                })()
                            } />

                            <Route path="/invite/:token" element={<Join user={user} />} />
                            <Route path="/join/:token" element={<Join user={user} />} />
                            <Route path="/add-friend" element={user ? <InviteManager user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/create" element={user ? <CreateHangout user={user} settings={settings} /> : <Navigate to="/" replace />} />
                            <Route path="/locations" element={user ? <Locations user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/squads" element={user ? <Squads user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/squad/:id" element={<SquadClubhouse user={user} settings={settings} />} />
                            <Route path="/edit/:id" element={user ? <EditHangout user={user} settings={settings} /> : <Navigate to="/" replace />} />
                            <Route path="/hangout/:id" element={<ViewHangout user={user} settings={settings} />} />
                            <Route path="/notifications" element={user ? <Notifications user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/notifications/settings" element={user ? <NotificationSettings user={user} /> : <Navigate to="/" replace />} />

                            {/* Deep Link Handler */}
                            <Route path="/feedback" element={<FeedbackDeepLinkHandler onOpen={() => setShowGlobalFeedback(true)} />} />

                            {/* Squad Sync (Protected) */}
                            <Route path="/polls/create" element={user ? <CreatePoll user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/polls/:id" element={user ? <PollResponse user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/me" element={user ? <Navigate to={`/u/${user.id}${window.location.search}`} replace /> : <Navigate to="/" replace />} />
                            {/* Authenticated Routes */}
                            <Route path="/u/:id" element={user ? <UserProfile currentUser={user} /> : <Navigate to="/" replace />} />
                            <Route path="/labs" element={<Labs user={user} onUserUpdate={refreshUser} />} />
                            <Route path="/whats-new" element={<WhatsNew user={user} />} />
                            <Route path="/about" element={user ? <About user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/feature-gallery-demo" element={<FeatureGalleryDemo />} />

                            {/* Developer Portal */}
                            <Route path="/developer">
                                <Route index element={
                                    settings['DEVELOPER_PORTAL_ENABLED'] === 'false'
                                        ? <Navigate to="/" replace />
                                        : (user ? <Navigate to="/developer/dashboard" replace /> : <DeveloperLanding />)
                                } />
                                <Route element={
                                    settings['DEVELOPER_PORTAL_ENABLED'] === 'false'
                                        ? <Navigate to="/" replace />
                                        : <DeveloperCoreLayout user={user} />
                                }>
                                    <Route path="dashboard" element={user && settings['DEVELOPER_PORTAL_ENABLED'] !== 'false' ? <DeveloperDashboard /> : <Navigate to="/developer" replace />} />
                                    <Route path="docs" element={user && settings['DEVELOPER_PORTAL_ENABLED'] !== 'false' ? <DeveloperDocs /> : <Navigate to="/developer" replace />} />
                                </Route>
                            </Route>

                            {/* Modular Garage Feature */}
                            <Route path="/garage" element={user ? <GarageDashboard user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/garage/search" element={user ? <GlobalSearchBar user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/garage/feed" element={user ? <UnifiedFeedPage user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/garage/workshop" element={user ? <WorkshopCenter user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/garage/reporting" element={user ? <ReportingDashboard user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/garage/settings" element={user ? <ProfileManager user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/garage/incident-wizard" element={user ? <IncidentWizard user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/garage/incidents/:id/edit" element={user ? <IncidentWizard user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/garage/incidents" element={user ? <IncidentVault user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/garage/incidents/:id" element={user ? <IncidentDetail user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/garage/:id" element={user ? <VehicleDetail user={user} /> : <Navigate to="/" replace />} />

                            {/* Modular Bark Spot Feature */}
                            <Route path="/bark" element={user ? <BarkDashboard user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/bark/feed" element={user ? <BarkFeed user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/bark/post/:token" element={<SharedBarkPost user={user} />} />
                            <Route path="/bark/discover" element={user ? <BarkDiscover user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/bark/lost-found" element={<LostFoundBoard user={user} />} />
                            <Route path="/bark/lost-found/:id" element={<LostPetDetail user={user} />} />
                            <Route path="/lost-found" element={<LostFoundBoard user={user} />} />
                            <Route path="/lost-found/:id" element={<LostPetDetail user={user} />} />
                            <Route path="/p/:slug" element={<PetPublicProfile user={user} />} />
                            <Route path="/bark/households/:id" element={user ? <HouseholdProfile user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/bark/reporting" element={user ? <BarkReportingDashboard user={user} /> : <Navigate to="/" replace />} />
                            <Route path="/bark/:id" element={user ? <PetDetail user={user} /> : <Navigate to="/" replace />} />

                            {/* Dynamic Topics */}
                            <Route path="/topic/:slug" element={<DynamicLanding settings={settings} />} />
                            <Route path="/topics" element={<TopicsDirectory />} />

                            {/* Admin Portal Gateway */}
                            <Route path="/admin" element={<AdminPortalRedirect />} />
                            <Route path="/admin-portal/*" element={<AdminPortalRedirect />} />

                            {/* Catch all */}
                            <Route path="*" element={<NotFoundHandler />} />
                        </Routes>
                    </Suspense>
                </main>

                {/* Persistent Bottom Navigation (Only for logged in users or if landing is NOT shown) */}
                {!showLanding && user && <Navigation user={user} settings={settings} />}
            </div>
        </Router>
    )
}


function TourTrigger({ user, pendingPolicies, showTour, setShowTour }) {
    const location = useLocation();

    useEffect(() => {
        // Only trigger tour if:
        // 1. User is logged in
        // 2. User hasn't seen the tour
        // 3. There are no pending policies (legal first!)
        // 4. We are on the Home page (/)
        // 5. Tour isn't already showing
        if (user && !user.hasSeenTour && pendingPolicies.length === 0 && location.pathname === '/' && !showTour) {
            setShowTour(true);
        }
    }, [user, pendingPolicies, location.pathname, showTour, setShowTour]);

    return null;
}

function NotFoundHandler() {
    const location = useLocation();

    useEffect(() => {
        trackEvent('PAGE_NOT_FOUND', { path: location.pathname });
    }, [location.pathname]);

    return (
        <div className="min-h-screen bg-neutral-950 text-white font-sans flex flex-col items-center justify-center p-6 text-center relative overflow-hidden">
            {/* Ambient Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-900/20 rounded-full blur-[120px] pointer-events-none" />

            <div className="relative z-10 max-w-md w-full bg-white/5 backdrop-blur-xl p-8 rounded-3xl border border-white/10 shadow-2xl flex flex-col items-center animate-fade-in">
                <div className="w-16 h-16 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 text-3xl mb-6 shadow-inner">
                    🛸
                </div>

                <h1 className="text-3xl font-black tracking-tight mb-2 text-white">
                    404 - Page Not Found
                </h1>

                <p className="text-sm text-neutral-400 mb-6 leading-relaxed">
                    The requested route <code className="text-purple-300 font-mono bg-black/40 px-2 py-0.5 rounded border border-white/10">{location.pathname}</code> does not exist or has been moved.
                </p>

                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold mb-8">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    Logged to telemetry for review
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <a
                        href="/"
                        className="flex-1 py-3 px-4 bg-white text-black font-bold rounded-xl text-sm hover:bg-neutral-200 transition text-center shadow-lg hover:scale-105 active:scale-95 cursor-pointer"
                    >
                        Return Home
                    </a>
                    <a
                        href="/topics"
                        className="flex-1 py-3 px-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-sm border border-white/10 transition text-center backdrop-blur-sm hover:scale-105 active:scale-95 cursor-pointer"
                    >
                        Explore Topics
                    </a>
                </div>
            </div>
        </div>
    );
}

function RedirectHandler() {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Post-login redirect for shared Bark posts or other deep links
        const rawPostRedirect = sessionStorage.getItem('post_login_redirect') || 
            localStorage.getItem('post_login_redirect') || 
            (document.cookie.match(/(?:^|;\s*)post_login_redirect=([^;]*)/)?.[1]);

        if (rawPostRedirect) {
            const decoded = decodeURIComponent(rawPostRedirect);
            if (decoded.startsWith('/bark/post/')) {
                if (location.pathname !== decoded) {
                    sessionStorage.removeItem('post_login_redirect');
                    localStorage.removeItem('post_login_redirect');
                    document.cookie = 'post_login_redirect=; path=/; max-age=0';
                    navigate(decoded, { replace: true });
                    return;
                }
            }
        }

        const params = new URLSearchParams(location.search);
        const urlInviteToken = params.get('invite');
        const pendingInviteToken = localStorage.getItem('pending_invite_token');
        const inviteToken = urlInviteToken || pendingInviteToken;

        if (inviteToken) {
            console.log('🔗 [RedirectHandler] Invite Token detected:', inviteToken);
            localStorage.removeItem('pending_invite_token');
            if (!location.pathname.startsWith('/invite/') && !location.pathname.startsWith('/join/')) {
                navigate(`/join/${inviteToken}`, { replace: true });
            } else if (urlInviteToken) {
                params.delete('invite');
                const remainingSearch = params.toString() ? `?${params.toString()}` : '';
                navigate(`${location.pathname}${remainingSearch}`, { replace: true });
            }
        }
    }, [location, navigate]);

    return null;
}

function FeedbackDeepLinkHandler({ onOpen }) {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        onOpen();
        // Preserve other query params if needed? For now just go home.
        navigate('/', { replace: true });
    }, [onOpen, navigate]);

    return null;
}

function WorkspaceContextWatcher({ user, settings }) {
    const location = useLocation();
    const { appContext, setAppContext } = useApp();

    useEffect(() => {
        const path = location.pathname;
        const host = window.location.hostname;
        const isWrenchHost = host.includes('wrenchspot.local') || host.includes('wrenchspot.app') || host.includes('wrenchspot');
        const isBarkHost = host.includes('barkspot.local') || host.includes('barkspot.app') || host.includes('barkspot');
        
        const isTopicPath = path.startsWith('/topic/') || path === '/topics' || path === '/landing';

        const isSwitching = sessionStorage.getItem('hs_switching_workspace') === 'true';

        const isDeveloperPath = path.startsWith('/developer');
        const isGaragePath = path.startsWith('/garage') || path.startsWith('/public/garage') || path.startsWith('/public/vehicle') || (path === '/' && isWrenchHost);
        const isBarkPath = path.startsWith('/bark') || (path === '/' && isBarkHost);
        const isHangoutPath = (path === '/' && !isWrenchHost && !isBarkHost) || 
            path.startsWith('/hangout') || 
            path.startsWith('/locations') || 
            path.startsWith('/create') || 
            path.startsWith('/polls') || 
            path.startsWith('/add-friend') || 
            path.startsWith('/invite') || 
            path.startsWith('/join');

        if (isDeveloperPath) {
            if (appContext !== 'DEVELOPER') {
                if (isSwitching) {
                    return;
                }
                setAppContext('DEVELOPER');
            } else {
                if (isSwitching) {
                    sessionStorage.removeItem('hs_switching_workspace');
                }
            }
        } else if (isGaragePath) {
            if (appContext !== 'WRENCH') {
                if (isSwitching) {
                    // Actively switching away from Wrench to Hangout, ignore this check
                    return;
                }
                setAppContext('WRENCH');
            } else {
                // Arrived in Wrench workspace, clear switching state if active
                if (isSwitching) {
                    sessionStorage.removeItem('hs_switching_workspace');
                }
            }
        } else if (isBarkPath) {
            if (appContext !== 'BARK') {
                if (isSwitching) {
                    return;
                }
                setAppContext('BARK');
            } else {
                if (isSwitching) {
                    sessionStorage.removeItem('hs_switching_workspace');
                }
            }
        } else if (isHangoutPath) {
            if (appContext !== 'HANGOUT') {
                if (isSwitching) {
                    // Actively switching away from Hangout to Wrench, ignore this check
                    return;
                }
                setAppContext('HANGOUT');
            } else {
                // Arrived in Hangout workspace, clear switching state if active
                if (isSwitching) {
                    sessionStorage.removeItem('hs_switching_workspace');
                }
            }
        }
    }, [location.pathname, appContext, setAppContext, user, settings]);

    return null;
}

function AdminPortalRedirect() {
    useEffect(() => {
        window.location.href = `/admin-portal/${window.location.search}`;
    }, []);

    return (
        <div className="min-h-screen bg-neutral-950 flex flex-col items-center justify-center p-6 text-white text-center">
            <div className="w-14 h-14 rounded-3xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 mb-4 animate-pulse shadow-xl">
                <Lock size={26} />
            </div>
            <h3 className="text-base font-black uppercase tracking-wider text-white mb-1">Redirecting to Admin Console</h3>
            <p className="text-xs text-neutral-400">Loading command center...</p>
        </div>
    );
}

export default App

